import { cleanConditionDisplay } from "@/lib/condition-display";

/** Strip test-data hex suffixes from chart labels (allergies, conditions, etc.). */
export function cleanClinicalLabel(display: string): string {
  return cleanConditionDisplay(display);
}

const SEVERITY_RANK: Record<string, number> = {
  mild: 1,
  low: 1,
  moderate: 2,
  severe: 3,
  high: 3,
  life_threatening: 4,
};

function severityRank(severity?: string | null): number {
  if (!severity) return 0;
  return SEVERITY_RANK[String(severity).toLowerCase()] ?? 0;
}

export function isActiveProblemStatus(status: string): boolean {
  return status === "active" || status === "recurrence" || status === "relapse";
}

/** One row per substance — keeps the highest documented severity. */
/** Stable key for the same allergen substance (code preferred, else type + cleaned name). */
export function allergySubstanceKey(parts: {
  allergen_display: string;
  allergen_code?: string | null;
  allergen_type?: string;
}): string {
  const code = parts.allergen_code?.trim().toLowerCase();
  const name = cleanClinicalLabel(parts.allergen_display).toLowerCase();
  const type = (parts.allergen_type ?? "unknown").toLowerCase();
  return code || `${type}:${name}`;
}

export function dedupeAllergiesBySubstance<
  T extends {
    id: string;
    allergen_display: string;
    severity?: string | null;
    allergen_code?: string | null;
    allergen_type?: string;
  },
>(items: T[]): T[] {
  const byKey = new Map<string, T>();
  for (const item of items) {
    const key = allergySubstanceKey(item);
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing || severityRank(item.severity) > severityRank(existing.severity)) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values());
}

/** One row per diagnosis code, or per cleaned label when code is missing. */
export function dedupeConditionsByDiagnosis<
  T extends { id: string; diagnosis_display: string; diagnosis_code?: string | null },
>(items: T[]): T[] {
  const byKey = new Map<string, T>();
  for (const item of items) {
    const code = item.diagnosis_code?.trim().toLowerCase();
    const label = cleanClinicalLabel(item.diagnosis_display).toLowerCase();
    const key = code || label;
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      continue;
    }
    if (!existing.diagnosis_code?.trim() && code) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values());
}
